import { ActionTypeKeys as CommonActionTypeKeys } from 'common/actions'
import { ActionTypeKeys, ActionTypes } from '../actions'

import { ApplicationStep, LifeInsuranceApplicationStep, LifeInsuranceResultsView, PlaidStatus } from 'embed/constants'
import { Config, CreditCardStep, DepositsStep, LifeInsuranceStep, LoanStep, Product } from 'even-ts-static'

export interface ConfigProviderState {
  availableProducts: Product[]
  hasLoadedInitialPartnerConfig: boolean
  hasCalledGeocoder: boolean
  isCCHybridOfferListEnabled?: boolean
  isLoadingPartnerConfig: boolean
  [Product.LifeInsurance]: LifeInsuranceUIState
  plaid: PlaidState
  previewIsInitializing: boolean
  selectedProduct?: Product
  selectedStep: SelectedStepState
  selectedStepHistory: SelectedStepHistoryState
  selectedApplicationStep: SelectedApplicationStepState
  viewport: { height?: number; width?: number }
}

export interface PlaidState {
  isModalOpen: boolean
  status: PlaidStatus
}

export interface SelectedStepState {
  [Product.CreditCard]: CreditCardStep
  [Product.Deposits]: DepositsStep
  [Product.Loan]: LoanStep
  [Product.LifeInsurance]: LifeInsuranceStep
}

export interface SelectedStepHistoryState {
  [Product.CreditCard]: CreditCardStep[]
  [Product.Deposits]: DepositsStep[]
  [Product.Loan]: LoanStep[]
  [Product.LifeInsurance]: LifeInsuranceStep[]
}

export interface SelectedApplicationStepState {
  [Product.Loan]?: ApplicationStep
  [Product.CreditCard]?: ApplicationStep
  [Product.LifeInsurance]?: LifeInsuranceApplicationStep
}

export interface LifeInsuranceUIState {
  isSelectedOfferLoaded: boolean
  resultsView: LifeInsuranceResultsView
  coverageAmount: number
  coverageDuration: number
}

export const initialUiState: ConfigProviderState = {
  availableProducts: [],
  hasCalledGeocoder: false,
  hasLoadedInitialPartnerConfig: false,
  isLoadingPartnerConfig: false,
  [Product.LifeInsurance]: {
    isSelectedOfferLoaded: false,
    resultsView: LifeInsuranceResultsView.Recommended,
    coverageAmount: 0,
    coverageDuration: 0,
  },
  plaid: {
    isModalOpen: false,
    status: PlaidStatus.NotConnected,
  },
  previewIsInitializing: true,
  selectedApplicationStep: {
    [Product.CreditCard]: undefined,
    [Product.Loan]: undefined,
  },
  selectedProduct: undefined,
  selectedStep: {
    [Product.Deposits]: DepositsStep.OfferList,
    [Product.Loan]: LoanStep.OfferList,
    [Product.CreditCard]: CreditCardStep.Intro,
    [Product.LifeInsurance]: LifeInsuranceStep.Preview,
  },
  selectedStepHistory: {
    [Product.Deposits]: [],
    [Product.Loan]: [],
    [Product.CreditCard]: [],
    [Product.LifeInsurance]: [],
  },
  viewport: {
    height: undefined,
    width: undefined,
  },
}

const getNewSelectedStepHistory = (state: ConfigProviderState, action: ActionTypes) => {
  if (action.type === CommonActionTypeKeys.STEP_SELECTED) {
    const oldStep = state.selectedStep[action.product]
    const shouldPopStepHistory = action.isSteppingBack
    const shouldPushToStepHistory =
      oldStep !== undefined &&
      state.selectedStepHistory[action.product]?.slice(-1)[0] !== oldStep &&
      oldStep !== action.step
    if (shouldPopStepHistory) {
      return {
        ...state.selectedStepHistory,
        [action.product]: state.selectedStepHistory[action.product].slice(0, -1),
      }
    } else if (shouldPushToStepHistory) {
      return {
        ...state.selectedStepHistory,
        [action.product]: [...state.selectedStepHistory[action.product], oldStep],
      }
    } else {
      return state.selectedStepHistory
    }
  }
  return state.selectedStepHistory
}

// eslint-disable-next-line complexity
const reducer = (state: ConfigProviderState = initialUiState, action: ActionTypes, config: Config) => {
  switch (action.type) {
    case CommonActionTypeKeys.CALLED_GEOCODER:
      return { ...state, hasCalledGeocoder: true }
    case CommonActionTypeKeys.LOAD_CONFIG_BEGIN:
      return { ...state, isLoadingPartnerConfig: true }
    case CommonActionTypeKeys.LOAD_CONFIG_SUCCESS:
      return { ...state, hasLoadedInitialPartnerConfig: true, isLoadingPartnerConfig: false }
    case CommonActionTypeKeys.PLAID_ERROR:
      return { ...state, plaid: { ...state.plaid, status: PlaidStatus.Error } }
    case CommonActionTypeKeys.PLAID_LOADING:
      if (action.loading) {
        return { ...state, plaid: { ...state.plaid, status: PlaidStatus.Loading } }
      } else {
        return { ...state, plaid: { ...state.plaid, status: PlaidStatus.NotConnected } }
      }
    case CommonActionTypeKeys.PLAID_MODAL_OPEN:
      return { ...state, plaid: { ...state.plaid, isModalOpen: action.isModalOpen } }
    case CommonActionTypeKeys.PLAID_SUCCESS:
      return { ...state, plaid: { ...state.plaid, status: PlaidStatus.Success } }
    case ActionTypeKeys.PRODUCT_SELECTED:
      const nextState = { ...state }
      // Reset the currently selected step to it's initial step
      if (typeof nextState.selectedProduct !== 'undefined') {
        nextState.selectedStep[nextState.selectedProduct] = initialUiState.selectedStep[nextState.selectedProduct]
      }

      return { ...nextState, selectedProduct: action.product }
    case ActionTypeKeys.SET_LIFE_INSURANCE_RESULTS_VIEW:
      return { ...state, [Product.LifeInsurance]: { ...state[Product.LifeInsurance], resultsView: action.resultsView } }
    case ActionTypeKeys.SET_LIFE_INSURANCE_COVERAGE_AMOUNT:
      return {
        ...state,
        [Product.LifeInsurance]: { ...state[Product.LifeInsurance], coverageAmount: action.coverageAmount },
      }
    case ActionTypeKeys.SET_LIFE_INSURANCE_COVERAGE_DURATION:
      return {
        ...state,
        [Product.LifeInsurance]: { ...state[Product.LifeInsurance], coverageDuration: action.coverageDuration },
      }
    case ActionTypeKeys.SET_LIFE_INSURANCE_OFFER: {
      return { ...state, [Product.LifeInsurance]: { ...state[Product.LifeInsurance], isSelectedOfferLoaded: true } }
    }
    case ActionTypeKeys.SET_AVAILABLE_PRODUCTS:
      return { ...state, availableProducts: action.products }
    case CommonActionTypeKeys.STEP_SELECTED:
      return {
        ...state,
        selectedProduct: action.product,
        selectedStep: { ...state.selectedStep, [action.product]: action.step },
        selectedStepHistory: getNewSelectedStepHistory(state, action),
      }
    case CommonActionTypeKeys.APPLICATION_STEP_SELECTED:
      return {
        ...state,
        selectedApplicationStep: { ...state.selectedApplicationStep, [action.product]: action.applicationStep },
        selectedProduct: action.product,
      }
    case CommonActionTypeKeys.ENABLE_HYBRID_STEP:
      return {
        ...state,
        isCCHybridOfferListEnabled: true,
      }
    case ActionTypeKeys.SET_VIEWPORT_SIZE:
      return { ...state, viewport: { height: action.height, width: action.width } }
    case CommonActionTypeKeys.FETCH_RATE_TABLES_START:
    case CommonActionTypeKeys.FETCH_PREVIEW_OFFERS_START:
      return { ...state, previewIsInitializing: false }
    default:
      return state
  }
}

export default reducer
